import React, { useEffect } from 'react';
import { Button, DatePicker, Form, Input, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useI18n } from '../i18n';

const { Option } = Select;
const { TextArea } = Input;

export default function ContactModal({
  modalType,
  isModalVisible,
  handleCancel,
  editingContact,
  currentView,
  selectedLead,
  selectedSupplier,
  leads,
  suppliers,
  onSave,
}) {
  const [form] = Form.useForm();
  const { t } = useI18n();
  const contactSource = Form.useWatch('type', form);

  useEffect(() => {
    if (!isModalVisible) return;

    const values = editingContact
      ? {
          clientId: editingContact.clientId,
          date: editingContact.date ? dayjs(editingContact.date) : null,
          type: editingContact.type,
          subject: editingContact.subject,
          description: editingContact.description,
          referredByClientId: editingContact.referredByClientId || '',
          eventName: editingContact.eventName || '',
          eventDate: editingContact.eventDate ? dayjs(editingContact.eventDate) : null,
          networkName: editingContact.networkName || '',
        }
      : {
          clientId: selectedLead?.id ?? selectedSupplier?.id,
          date: dayjs(),
          type: undefined,
          subject: '',
          description: '',
          referredByClientId: undefined,
          eventName: '',
          eventDate: null,
          networkName: '',
        };

    form.setFieldsValue(values);
  }, [editingContact, form, isModalVisible, selectedLead, selectedSupplier]);

  return (
    <Modal
      title={modalType === 'createContact' ? t('Add Contact Record') : t('Edit Contact Record')}
      open={isModalVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>{t('Close')}</Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => {
            form
              .validateFields()
              .then((values) => {
                onSave(values);
                handleCancel();
                message.success(t(modalType === 'createContact' ? 'Contact record created' : 'Contact record updated'));
              })
              .catch(() => {});
          }}
        >
          {modalType === 'createContact' ? t('Create') : t('Update')}
        </Button>,
      ]}
      width={700}
    >
      <Form form={form} layout="vertical">
        {(currentView === 'contacts' || (!selectedLead && !selectedSupplier)) && (
          <Form.Item
            label={t('Select Counterparty Name')}
            name="clientId"
            rules={[{ required: true, message: t('Please select counterparty') }]}
          >
            <Select showSearch placeholder={t('Search by name')}>
              {leads.map(lead => (
                <Option key={lead.key} value={lead.id}>
                  {lead.name}
                </Option>
              ))}
              {suppliers.map((supplier) => (
                <Option key={`supplier-${supplier.id}`} value={supplier.id}>
                  {supplier.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          label={t('Date')}
          name="date"
          rules={[{ required: true, message: t('Please select date') }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label={t('Contact Source')}
          name="type"
          rules={[{ required: true, message: t('Please select contact source') }]}
        >
          <Select placeholder={t('Select contact source')}>
            <Option value="Email">{t('Email')}</Option>
            <Option value="Phone">{t('Phone')}</Option>
            <Option value="Network">{t('Network')}</Option>
            <Option value="Fair/Event">{t('Fair/Event')}</Option>
            <Option value="Referral">{t('Referral')}</Option>
          </Select>
        </Form.Item>
        {contactSource === 'Referral' && (
          <Form.Item
            label={t('Referred by')}
            name="referredByClientId"
            rules={[{ required: true, message: t('Please select who referred this contact') }]}
          >
            <Select
              showSearch
              placeholder={t('Select existing client')}
              optionFilterProp="children"
            >
              {leads.map((lead) => (
                <Option key={lead.key} value={lead.id}>
                  {lead.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {contactSource === 'Fair/Event' && (
          <>
            <Form.Item
              label={t('Event name')}
              name="eventName"
              rules={[{ required: true, message: t('Please enter event name') }]}
            >
              <Input placeholder={t('Event or fair name')} />
            </Form.Item>
            <Form.Item
              label={t('Event date')}
              name="eventDate"
              rules={[{ required: true, message: t('Please select event date') }]}
            >
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}
        {contactSource === 'Network' && (
          <Form.Item
            label={t('Network name')}
            name="networkName"
            rules={[{ required: true, message: t('Please enter network name') }]}
          >
            <Input placeholder={t('Network name')} />
          </Form.Item>
        )}
        <Form.Item
          label={t('Subject')}
          name="subject"
          rules={[{ required: true, message: t('Please enter subject') }]}
        >
          <Input placeholder={t('Brief description')} />
        </Form.Item>
        <Form.Item label={t('Description')} name="description">
          <TextArea rows={4} placeholder={t('Detailed notes...')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
